import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Button } from "@material-tailwind/react";

import { Issue } from "../Issue";
import { filteringController } from "../../Controllers/FilteringController";
import { StockistIssue } from "../../../../Common/DTOs/StockistIssue";
import { stockiestPortalController } from "../../../../Common/Controllers/StockiestPortalController";
import { issueDetailsController } from "../../Controllers/IssueDetailsController";

// TODO: the list logic could be extracted into a different component
export const AllIssuesOutlet = observer(() => {
  const lastIssueRef = useRef<HTMLDivElement | null>(null);

  const isLoading =
    (filteringController.currentTab === "latest" && filteringController.loadingIssues) ||
    (filteringController.currentTab === "extras" && stockiestPortalController.isIssuesForExtrasLoading);

  useEffect(() => {
    if (lastIssueRef.current) {
      const observer = new IntersectionObserver(
        async (entries) => {
          if (entries[0].isIntersecting && !filteringController.allIssuesPulled && !stockiestPortalController.loadingIssues) {
            await filteringController.pullMoreIssues();
          }
        },
        { threshold: 0 }
      );

      observer.observe(lastIssueRef.current!);
      return () => observer.disconnect();
    }
  }, [lastIssueRef, filteringController.filteredIssues]);

  return (
    <>
      <div id={`${filteringController.currentTab}`} className="px-3 md:px-8 flex justify-center md:justify-end"></div>
      {filteringController.filteredIssues && (
        <div className="lg:px-2 w-full grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] justify-items-center gap-x-1 gap-y-8 py-2 mt-2">
          {filteringController.filteredIssues.length > 0 ? (
            <>
              {filteringController.filteredIssues.map((issue, index) => (
                <Issue
                  onlyThumbnail={false}
                  issue={issue}
                  key={issue.id}
                  TitleDetails={TitleDetails}
                  ref={
                    index === filteringController.filteredIssues.length - 1
                      ? lastIssueRef
                      : null
                  }
                />
              ))}
              {isLoading && Array.from({ length: 4 }).map((_, i) => (
                <div className="animate-fadeIn" key={`skel-${i}`} >
                  <IssueSkeleton />
                </div>
              ))}
            </>
          ) : isLoading ? (
            Array.from({ length: 12 }).map((_, i) => (
              <div className="animate-fadeIn" key={`skel-${i}`}>
                <IssueSkeleton />
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col animate-fadeUp items-center mt-8">
              <div className="text-center text-gray-400">No issues found</div>
            </div>
          )}
        </div>
      )}
    </>
  )
})

const IssueSkeleton = () => (
  <div className="flex flex-col items-center justify-between w-full px-2 xsm:px-0 xsm:w-[230px] h-[489px] animate-pulse">
    <div className="w-full h-[90%] flex flex-col items-center sm:items-start gap-0.5">
      <div className="w-[230px] h-[320px] bg-gray-300/20 mx-auto" />
      <div className="flex flex-col mt-4 gap-2 w-full items-center xsm:items-start justify-between flex-auto">
        <div className="flex flex-col items-center xsm:items-start gap-1 w-full">
          <div className="w-3/4 h-5 bg-gray-300/20 rounded" />
          <div className="w-full h-4 bg-gray-300/20 rounded" />
        </div>
        <div className="w-1/2 h-5 bg-gray-300/20 rounded mb-1" />
      </div>
    </div>
    <div className="w-full flex flex-row gap-1 pt-2 border-t border-gray-800">
      <div className="h-[50px] flex-1 bg-gray-300/20 rounded-full" />
      <div className="h-[50px] flex-1 bg-gray-300/20 rounded-full" />
    </div>
  </div>
);

type IssueDetailsProps = {
  issue: StockistIssue
}

export const TitleDetails = observer(({ issue }: IssueDetailsProps) => {
  const navigate = useNavigate()

  return (
    <div className="w-full h-full flex flex-col animate-fadeUp items-center">
      <div className="overflow-y-scroll">{issue.stockistIssueText ?? ""}</div>
      <div className="w-full">
        <div className="border-t-2 my-2" />
        <div className="flex flex-col gap-4">
          {issue.mostSpecificDiscounts && issue.mostSpecificDiscounts.length > 0 && (
            <div className="flex flex-row justify-between items-center gap-4">
              <div>Discount:</div>
              <div className="text-green-500 text-lg font-semibold transition duration-150">
                Up to -{Math.max(...issue.mostSpecificDiscounts.map(d => d.discountPercentage))}%
              </div>
            </div>
          )}
          <div className="flex flex-row justify-center items-center gap-4">
            <div className="basis-[60%]">Weight: {issue.weightKilograms}kg per copy</div>
            <div className="basis-[40%]">
              <Button variant={"outlined"} color={"white"} size={"sm"} onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); }}>More info</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});